import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useProfileUpdate } from '../hooks/useProfileUpdate';
import { useRiskReportStore } from '../stores/riskReportStore';
import { useSavedPortfolioStore } from '../stores/savedPortfolioStore';
import { useGuestOnboardingStore } from '../stores/guestOnboardingStore';
import { useLocation } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import analyticsService from '../services/analytics';
import { useLevelUpStore } from '../stores/levelUpStore';
import { useNotificationStore } from '../stores/notificationStore';
import type { OnboardingTasks, OnboardingTaskId } from '../types/user';

// Task metadata for display
interface TaskMeta {
  id: OnboardingTaskId;
  title: string;
  subtitle: string;
  icon: 'quiz' | 'search' | 'visibility' | 'folder' | 'chat' | 'shield';
}

// The order IS the funnel — ACQUIRE → EDUCATE → TASTE → CONVERT (PRODUCT.md),
// re-sequenced 2026-08-24 (ROADMAP 1.8). It used to open with the profile quiz
// and reach the portfolio fourth of five, which asked for personal details
// before the product had shown anything worth personalising.
//
// `firstWatchlist` was replaced by `firstRiskAnalysis`: My Watchlist was hidden
// from the profile menu (2026-08-23), and a checklist pointing at a feature we
// have deliberately stopped surfacing is a dead end that costs a star nobody
// can earn cleanly. A risk analysis is the free tier's TASTE step — the manual
// version of what Signals runs for you.
const TASK_DEFINITIONS: TaskMeta[] = [
  { id: 'firstSearch', title: 'Research your first stock', subtitle: 'The quickest look at any company', icon: 'search' },
  { id: 'firstChat', title: 'Ask the AI assistant', subtitle: 'Get answers in seconds', icon: 'chat' },
  { id: 'firstPortfolio', title: 'Upload your portfolio', subtitle: 'Everything below works from it', icon: 'folder' },
  { id: 'firstRiskAnalysis', title: 'Run a risk analysis', subtitle: 'See what your book is carrying', icon: 'shield' },
  { id: 'quizCompleted', title: 'Complete your profile quiz', subtitle: 'Tune it to how you invest', icon: 'quiz' },
];

const TOTAL_STARS = 5;
const BOOLEAN_TASK_IDS: OnboardingTaskId[] = [
  'quizCompleted', 'firstSearch', 'firstRiskAnalysis', 'firstPortfolio', 'firstChat',
];

/** Carry a star earned under the old checklist across the rename (ROADMAP 1.8).
 *
 *  The persisted per-user progress holds `firstWatchlist`. Dropping it would
 *  silently take a star back — and with it a level, since `computeLevel`
 *  gates on `starsEarned >= 5`. So the old flag maps onto the new id, and the
 *  star total stays 5 either way. */
function migrateTaskIds(tasks: OnboardingTasks): OnboardingTasks {
  if (tasks.firstRiskAnalysis || !tasks.firstWatchlist) return tasks;
  const migrated = { ...tasks, firstRiskAnalysis: true };
  return { ...migrated, starsEarned: computeStarsFromTasks(migrated) };
}

// Guest task IDs that can be completed without authentication
const GUEST_TASK_IDS: ReadonlySet<string> = new Set(['quizCompleted', 'firstSearch', 'firstChat']);

type GuestTaskId = 'quizCompleted' | 'firstSearch' | 'firstChat';

/** Derive star count from boolean flags — self-healing, never desyncs. */
function computeStarsFromTasks(tasks: OnboardingTasks): number {
  return BOOLEAN_TASK_IDS.filter((id) => tasks[id] === true).length;
}

interface OnboardingChecklistContextValue {
  // Task state
  tasks: OnboardingTasks;
  taskMetas: (TaskMeta & { completed: boolean })[];
  starsEarned: number;
  totalStars: number;
  allComplete: boolean;

  // UI state
  isVisible: boolean;
  isCollapsed: boolean;
  isGuest: boolean;

  // Celebration state
  showStarCelebration: boolean;
  celebrationTaskTitle: string | null;
  dismissCelebration: () => void;

  // Actions
  completeTask: (taskId: OnboardingTaskId) => void;
  dismissChecklist: () => void;
  toggleCollapse: () => void;

  // Legacy dialog support (for backwards compatibility)
  activeDialog: 'watchlist' | 'portfolio' | null;
  openDialog: (dialog: 'watchlist' | 'portfolio') => void;
  closeDialog: () => void;
}

const OnboardingChecklistContext = createContext<OnboardingChecklistContextValue | null>(null);

const WELCOME_KEY = 'discovery_quiz_welcome';
const QUIZ_COMPLETED_KEY = 'discovery_quiz_completed';
const GUEST_DISMISSED_KEY = 'indexalpha-onboarding-dismissed';

const DEFAULT_TASKS: OnboardingTasks = {
  quizCompleted: false,
  firstSearch: false,
  firstWatchlist: false,
  firstRiskAnalysis: false,
  firstPortfolio: false,
  firstChat: false,
  starsEarned: 0,
};

export const OnboardingChecklistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, userAccount } = useAuth();
  const { updateProfile } = useProfileUpdate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const riskReports = useRiskReportStore((s) => s.reports);
  const portfolios = useSavedPortfolioStore((s) => s.portfolios);
  const portfoliosLoading = useSavedPortfolioStore((s) => s.isLoading);

  // Guest onboarding store (localStorage-backed)
  const completeGuestTask = useGuestOnboardingStore((s) => s.completeGuestTask);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeDialog, setActiveDialog] = useState<'watchlist' | 'portfolio' | null>(null);
  const [showStarCelebration, setShowStarCelebration] = useState(false);
  const [celebrationTaskTitle, setCelebrationTaskTitle] = useState<string | null>(null);
  const [localTasks, setLocalTasks] = useState<OnboardingTasks | null>(null);
  /* The dismissal, in THIS session ([[BUGS #189]]).
     Durability lives on the profile; visibility must not wait for it. The
     panel used to hide only when `onboardingDismissed` came back through the
     Firestore snapshot, so a slow write delayed the close and a failed one
     never closed it at all — the reader clicks X and nothing happens, which
     reads as a control that was never wired up. On staging it cannot come
     back by construction: the browser's snapshot watches the PRODUCTION user
     document (Auth is shared, so `deploy.yml` pins the client to the prod
     project) while `update-profile` writes the staging one ([[BUGS #190]]). */
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  // Track whether we've already merged guest progress for this auth session
  const guestMergedRef = useRef(false);

  // ─── Sync local state from Firestore OR guest localStorage ───
  useEffect(() => {
    if (userAccount?.onboardingTasks) {
      // Authenticated: use Firestore data
      setLocalTasks(migrateTaskIds(userAccount.onboardingTasks));
    } else if (userAccount?.onboardingChecklist) {
      // Migration: convert legacy onboardingChecklist to new onboardingTasks
      const legacy = userAccount.onboardingChecklist;
      const migrated: OnboardingTasks = {
        ...DEFAULT_TASKS,
        firstWatchlist: legacy.watchlistCompleted ?? false,
        firstPortfolio: legacy.portfolioCompleted ?? false,
        starsEarned: (legacy.watchlistCompleted ? 1 : 0) + (legacy.portfolioCompleted ? 1 : 0),
      };
      setLocalTasks(migrated);
    } else if (!user) {
      // Guest mode: hydrate from guest onboarding store (localStorage)
      const guestTasks = useGuestOnboardingStore.getState().getGuestTasks();
      const hydrated: OnboardingTasks = {
        ...DEFAULT_TASKS,
        ...guestTasks,
        starsEarned: Object.values(guestTasks).filter(Boolean).length,
      };
      setLocalTasks(hydrated);
    }
  }, [user, userAccount?.onboardingTasks, userAccount?.onboardingChecklist]);

  // ─── Merge guest progress into Firestore on login ───
  useEffect(() => {
    if (!user || !userAccount || guestMergedRef.current) return;

    const guestStore = useGuestOnboardingStore.getState();
    if (!guestStore.hasAnyProgress()) {
      guestMergedRef.current = true;
      return;
    }

    const guestTasks = guestStore.getGuestTasks();
    const currentTasks = userAccount.onboardingTasks ?? DEFAULT_TASKS;

    // Merge with OR logic: if either guest or Firestore says completed, it's completed
    const merged: OnboardingTasks = {
      ...currentTasks,
      quizCompleted: currentTasks.quizCompleted || guestTasks.quizCompleted,
      firstSearch: currentTasks.firstSearch || guestTasks.firstSearch,
      firstChat: currentTasks.firstChat || guestTasks.firstChat,
      starsEarned: 0, // will be recomputed
    };
    merged.starsEarned = computeStarsFromTasks(merged);

    // Check if any new completions to write
    const hasNewCompletions =
      (!currentTasks.quizCompleted && guestTasks.quizCompleted) ||
      (!currentTasks.firstSearch && guestTasks.firstSearch) ||
      (!currentTasks.firstChat && guestTasks.firstChat);

    if (hasNewCompletions) {
      setLocalTasks(merged);
      updateProfile({ onboardingTasks: merged });
    }

    // Clear guest store and dismissed flag
    guestStore.clearGuestTasks();
    try { localStorage.removeItem(GUEST_DISMISSED_KEY); } catch { /* ignore */ }

    guestMergedRef.current = true;
  }, [user, userAccount, updateProfile]);

  // Reset merge flag when user logs out
  useEffect(() => {
    if (!user) {
      guestMergedRef.current = false;
    }
  }, [user]);

  // ─── Auto-detect completed tasks from existing data ───
  useEffect(() => {
    // Wait for stores to finish loading (watchlist/portfolio are auth-only but
    // we still need to wait so we don't run partial detection)
    if (portfoliosLoading) return;

    // For authenticated users, wait until Firestore data has loaded into localTasks.
    // Without this guard, localTasks is null while userAccount is still loading,
    // causing DEFAULT_TASKS (all false) to overwrite the real Firestore state.
    if (user && !localTasks) return;

    const currentTasks = localTasks ?? DEFAULT_TASKS;
    let needsUpdate = false;
    const updates: Partial<OnboardingTasks> = {};

    // Check quiz completion from localStorage (works for both guest and auth)
    try {
      const quizCompleted = localStorage.getItem(QUIZ_COMPLETED_KEY) === 'true';
      if (quizCompleted && !currentTasks.quizCompleted) {
        updates.quizCompleted = true;
        needsUpdate = true;
      }
    } catch { /* ignore */ }

    // Watchlist and portfolio checks only for authenticated users
    if (user) {
      // Check both watchlist store (saved_portfolios) and user profile watchlist array
      // A saved risk report IS the signal — the store already knows, so this is
      // a read rather than new tracking (ROADMAP 1.8).
      if (riskReports.length > 0 && !currentTasks.firstRiskAnalysis) {
        updates.firstRiskAnalysis = true;
        needsUpdate = true;
      }

      const realPortfolios = portfolios.filter((p: any) => !p.portfolio_type || p.portfolio_type !== 'watchlist');
      if (realPortfolios.length > 0 && !currentTasks.firstPortfolio) {
        updates.firstPortfolio = true;
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      const merged: OnboardingTasks = {
        ...currentTasks,
        ...updates,
        starsEarned: computeStarsFromTasks({ ...currentTasks, ...updates } as OnboardingTasks),
      };
      setLocalTasks(merged);
      if (user) {
        updateProfile({ onboardingTasks: merged });
      }
    }
  }, [user, userAccount, riskReports, portfolios, portfoliosLoading, localTasks, updateProfile]);

  const tasks = localTasks ?? DEFAULT_TASKS;
  // Always derive starsEarned from boolean flags — self-healing, never desyncs
  const starsEarned = computeStarsFromTasks(tasks);
  const allComplete = starsEarned >= TOTAL_STARS;
  const isGuest = !user;

  // Build task metas with completion status
  const taskMetas = useMemo(() =>
    TASK_DEFINITIONS.map((meta) => ({
      ...meta,
      completed: tasks[meta.id] ?? false,
    })),
    [tasks]
  );

  // ─── Visibility conditions ───
  const isVisible = useMemo(() => {
    // Auth user with account still loading — wait
    if (user && !userAccount) return false;
    if (isMobile) return false;
    if (location.pathname === '/chat') return false;
    if (location.pathname.startsWith('/portfolio/')) return false;
    // Admin tooling — onboarding prompts are noise here, the audience
    // is internal admins who already know the product.
    if (location.pathname === '/index_admin') return false;
    if (allComplete && !showStarCelebration) return false;
    // Delay if PostLoginWelcome is active
    try {
      if (localStorage.getItem(WELCOME_KEY)) return false;
    } catch { /* ignore */ }
    // Dismissed — the durable record, per audience. A signed-in dismissal used
    // to be inferred from `allComplete` above, which is why it could only be
    // stored by forging five completions ([[BUGS #185]]).
    // Closed in this session — believed immediately, whatever the write does.
    if (dismissedThisSession) return false;
    if (user) {
      if (userAccount?.onboardingDismissed) return false;
    } else {
      try {
        if (localStorage.getItem(GUEST_DISMISSED_KEY) === 'true') return false;
      } catch { /* ignore */ }
    }
    return true;
  }, [user, userAccount, isMobile, location.pathname, allComplete, showStarCelebration, dismissedThisSession]);

  // ─── Complete a task ───
  const completeTask = useCallback((taskId: OnboardingTaskId) => {
    if (tasks[taskId]) return; // Already completed

    const taskMeta = TASK_DEFINITIONS.find((t) => t.id === taskId);
    const updated: OnboardingTasks = {
      ...tasks,
      [taskId]: true,
      starsEarned: computeStarsFromTasks({ ...tasks, [taskId]: true } as OnboardingTasks),
      lastTaskCompletedAt: new Date(),
    };

    // Update local React state (immediate UI feedback)
    setLocalTasks(updated);

    // Persist based on auth state
    if (user) {
      updateProfile({ onboardingTasks: updated });
    } else if (GUEST_TASK_IDS.has(taskId)) {
      // Guest: persist to localStorage via Zustand store
      completeGuestTask(taskId as GuestTaskId);
    }

    analyticsService.trackOnboardingTaskCompleted(taskId, updated.starsEarned, TOTAL_STARS);

    // Show star celebration
    setCelebrationTaskTitle(taskMeta?.title ?? 'Task');
    setShowStarCelebration(true);

    // Auto-dismiss after 2.5 seconds
    setTimeout(() => {
      setShowStarCelebration(false);
      setCelebrationTaskTitle(null);
    }, 2500);
  }, [tasks, user, updateProfile, completeGuestTask]);

  const dismissCelebration = useCallback(() => {
    setShowStarCelebration(false);
    setCelebrationTaskTitle(null);
  }, []);

  // ─── Dismiss checklist ───
  //
  // **Dismissal is recorded as dismissal** ([[BUGS #185]], owner 2026-08-29:
  // *"dismiss action should be treated as a 'read' indicator… so they don't
  // just keep re-triggering every time user lands"*).
  //
  // It used to be recorded by marking all five tasks complete and setting
  // `starsEarned: TOTAL_STARS`. That conflated "I don't want this" with "I did
  // all of it", and it had a second consequence nobody would predict from
  // reading it: the star-sync effect below fires the **Level 1 Unlocked**
  // notification on the 0 → TOTAL_STARS transition, so **closing the panel
  // awarded a badge**. The person dismissed one prompt and was handed another.
  //
  // Progress is now left exactly as it was — someone who dismisses at two stars
  // still has two stars, and the Academy still knows it.
  const dismissChecklist = useCallback(() => {
    // Close first. The durable record is what stops it returning tomorrow; it
    // is not what closes the panel today ([[BUGS #189]]).
    setDismissedThisSession(true);
    if (user) {
      updateProfile({ onboardingDismissed: true });
    } else {
      // Guest: the localStorage flag is the whole record. It used to ALSO mark
      // three tasks complete, which then merged into the account on sign-up —
      // so dismissing as a guest silently granted stars to the user they later
      // became.
      try { localStorage.setItem(GUEST_DISMISSED_KEY, 'true'); } catch { /* ignore */ }
    }
    analyticsService.trackChecklistDismissed(starsEarned, TOTAL_STARS);
  }, [user, starsEarned, updateProfile]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const openDialog = useCallback((dialog: 'watchlist' | 'portfolio') => {
    setActiveDialog(dialog);
  }, []);

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
  }, []);

  // ─── Sync stars to levelUpStore (bridge between onboarding and gamification) ───
  //
  // **The baseline is the first HYDRATED reading, not the first render**
  // ([[BUGS #185]]). `useRef(starsEarned)` captured the value before Firestore
  // resolved — `localTasks` is null until an effect fills it, so `starsEarned`
  // is 0 on first render for a user who already has five stars. Every page load
  // therefore looked like 0 → 5 and re-fired "Level 1 Unlocked!", and the only
  // thing preventing a duplicate row was the store's title de-duplication —
  // which a person defeats simply by deleting the notification, at which point
  // it returns on their next landing. The comment here claimed the opposite of
  // what the code did.
  //
  // `null` until hydrated, and the first hydrated reading is recorded as a
  // BASELINE rather than treated as an event.
  const prevStarsRef = useRef<number | null>(null);
  useEffect(() => {
    // Forget the baseline when the account changes, or a second person inherits
    // the first one's star count and is congratulated for it.
    prevStarsRef.current = null;
  }, [user?.uid]);
  useEffect(() => {
    if (!user) return; // Only sync for authenticated users
    if (!localTasks) return; // Not hydrated — there is nothing to compare against
    const prevStars = prevStarsRef.current;
    prevStarsRef.current = starsEarned;
    useLevelUpStore.getState().syncStarsEarned(starsEarned);
    if (prevStars === null) return; // First real reading is the baseline

    // Fire Level 1 notification only when the user earns the 5th star in this session
    if (starsEarned >= TOTAL_STARS && prevStars < TOTAL_STARS) {
      useNotificationStore.getState().addNotification(user.uid, {
        type: 'level_up',
        title: 'Level 1 Unlocked!',
        message: 'You completed all onboarding tasks. Head to the Academy to take your first quiz and reach Level II.',
      });
    }
  }, [user, starsEarned, localTasks]);

  // Log when checklist is first shown
  useEffect(() => {
    if (isVisible && !allComplete) {
      analyticsService.trackChecklistShown(starsEarned, TOTAL_STARS);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const value = useMemo(
    () => ({
      tasks,
      taskMetas,
      starsEarned,
      totalStars: TOTAL_STARS,
      allComplete,
      isVisible,
      isCollapsed,
      isGuest,
      showStarCelebration,
      celebrationTaskTitle,
      dismissCelebration,
      completeTask,
      dismissChecklist,
      toggleCollapse,
      activeDialog,
      openDialog,
      closeDialog,
    }),
    [tasks, taskMetas, starsEarned, allComplete, isVisible, isCollapsed, isGuest, showStarCelebration, celebrationTaskTitle, dismissCelebration, completeTask, dismissChecklist, toggleCollapse, activeDialog, openDialog, closeDialog],
  );

  return (
    <OnboardingChecklistContext.Provider value={value}>
      {children}
    </OnboardingChecklistContext.Provider>
  );
};

export const useOnboardingChecklist = () => {
  const context = useContext(OnboardingChecklistContext);
  if (!context) {
    throw new Error('useOnboardingChecklist must be used within OnboardingChecklistProvider');
  }
  return context;
};

/**
 * Safe version that returns null if context is not available.
 * Use this in components that may render before/outside the provider.
 */
export const useOnboardingChecklistSafe = () => {
  return useContext(OnboardingChecklistContext);
};
